import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// ── Lazy: ALL pages (reduces main.js entrypoint by ~120KB disk / ~55KB gzipped)
// Rationale: Creator landing (/creator/:slug) is the viral entry point and must NOT
// download marketing page code (NicheLanding, EarlyAccess, DemoPage) just to render.
// With content-hash filenames + Cache-Control: immutable, subsequent visits cost 0 bytes.
const Landing      = lazy(() => import('./pages/Landing'));
const NicheLanding = lazy(() => import('./pages/niches/NicheLanding'));
const EarlyAccess  = lazy(() => import('./pages/EarlyAccess'));
const DemoPage     = lazy(() => import('./pages/demo/DemoPage'));
const Pricing      = lazy(() => import('./pages/Pricing'));
const Resources    = lazy(() => import('./pages/Resources'));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail'));

// ── Lazy: authenticated / non-critical routes ──
const Login            = lazy(() => import('./pages/Login'));
const SetPassword      = lazy(() => import('./pages/SetPassword'));
const CreatorsList     = lazy(() => import('./pages/CreatorsList'));
const CreatorLanding   = lazy(() => import('./pages/CreatorLanding'));
const CreatorEnquiry   = lazy(() => import('./pages/CreatorEnquiry'));
const AdminDashboard   = lazy(() => import('./pages/admin/Dashboard'));
const CreatorDashboard = lazy(() => import('./pages/creator/Dashboard'));
const StartTrial       = lazy(() => import('./pages/StartTrial'));
const TrialSuccess     = lazy(() => import('./pages/TrialSuccess'));
const TrialLeads       = lazy(() => import('./pages/admin/TrialLeads'));
const ContactUs        = lazy(() => import('./pages/ContactUs'));
const ProductLanding   = lazy(() => import('./pages/ProductLanding'));
const PrivacyPolicy    = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions  = lazy(() => import('./pages/TermsConditions'));
const RefundPolicy     = lazy(() => import('./pages/RefundPolicy'));
const BookingStatus    = lazy(() => import('./pages/BookingStatus'));
const CheckoutPage     = lazy(() => import('./pages/CheckoutPage'));

// ── Fallback spinner shown during lazy chunk fetch ──
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <p className="text-muted-foreground text-sm">Loading…</p>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // If no user (logged out), redirect to landing page instead of login
  if (!user) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // Role check: superadmin has access to admin routes
  if (requiredRole) {
    const hasAccess = 
      user.role === requiredRole || 
      (requiredRole === 'admin' && user.role === 'superadmin');
    
    if (!hasAccess) {
      return <Navigate to="/login" replace />;
    }
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/creators" element={<CreatorsList />} />
      <Route path="/creator/:slug" element={<CreatorLanding />} />
      <Route path="/book/:creatorSlug/:eventId" element={<CheckoutPage />} />
      <Route path="/apply" element={<CreatorEnquiry />} />
      <Route path="/products/:slug" element={<ProductLanding />} />
      <Route path="/resources/:slug" element={<ArticleDetail />} />

      {/* Growth Funnel Routes */}
      <Route path="/early-access" element={<EarlyAccess />} />

      {/* Use-Case Niche Pages (lazy-loaded via NicheLanding) */}
      <Route path="/for-specialists" element={<NicheLanding />} />
      <Route path="/for-fitness"     element={<NicheLanding />} />
      <Route path="/for-creators"    element={<NicheLanding />} />

      {/* Sales Gallery Demo Routes — no backend, instant load */}
      <Route path="/demo/:niche" element={<DemoPage />} />
      
      {/* Trial Routes (Public) */}
      <Route path="/start-trial" element={<StartTrial />} />
      <Route path="/start-trial/success" element={<TrialSuccess />} />
      
      {/* Public Booking Status */}
      <Route path="/booking/:bookingId/status" element={<BookingStatus />} />
      
      {/* Legal/Compliance Pages */}
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/refunds" element={<RefundPolicy />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Superadmin Trial Management */}
      <Route
        path="/admin/trial-leads"
        element={
          <ProtectedRoute requiredRole="superadmin">
            <TrialLeads />
          </ProtectedRoute>
        }
      />

      {/* Creator Routes */}
      <Route
        path="/creator/dashboard"
        element={
          <ProtectedRoute requiredRole="creator">
            <CreatorDashboard />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <div className="App">
            <AppRoutes />
            <Toaster position="top-right" />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;